import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, ArrowRight, Building2, UserPlus, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, login } = useAuth();
  const { toast } = useToast();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email.trim().toLowerCase(), password);
    if (result.success) {
      toast({ title: "Connexion reussie", description: "Bienvenue sur SecurESSI-AI" });
    } else {
      toast({ title: "Erreur", description: result.error, variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="login-page">
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-20 w-60 h-60 bg-blue-500/8 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 right-10 w-40 h-40 bg-cyan-400/5 rounded-full blur-2xl" />
      </div>

      {/* Content */}
      <div className="relative flex-1 flex flex-col px-6 pt-12 pb-6 max-w-md mx-auto w-full">
        
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25 mb-5">
            <img 
              src="https://customer-assets.emergentagent.com/job_0f1068d2-e428-4abf-9ea6-c10e13458ae4/artifacts/khbka7ja_te%CC%81le%CC%81chargement%20%2845%29.jpeg" 
              alt="SecurESSI-AI" 
              className="w-14 h-14 object-contain rounded-xl"
            />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">SecurESSI-AI</h1>
          <p className="text-sm text-slate-400 mt-1">Plateforme IA Securite Privee</p>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-xs text-cyan-400 font-medium">ALFA Federe - 5 Moteurs IA</span>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="space-y-4 mb-6">
            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                autoComplete="email"
                className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all text-sm"
                data-testid="login-email"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  required
                  className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all text-sm pr-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors p-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            data-testid="login-submit"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Se connecter
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          {/* Forgot password */}
          <div className="text-center mt-4">
            <a
              href="/forgot-password"
              className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
              data-testid="forgot-password-link"
            >
              Mot de passe oublié ?
            </a>
          </div>

          {/* Separator */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-slate-500">ou</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Register buttons */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => window.location.href = '/register-company'}
              className="w-full py-3.5 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/8 active:scale-[0.98] transition-all flex items-center px-4 gap-3"
              data-testid="register-company-btn"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-medium">Inscrire ma societe</p>
                <p className="text-[11px] text-slate-500">Creer un compte entreprise</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>

            <button
              type="button"
              onClick={() => window.location.href = '/register-admin'}
              className="w-full py-3.5 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/8 active:scale-[0.98] transition-all flex items-center px-4 gap-3"
              data-testid="register-user-btn"
            >
              <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-4 h-4 text-violet-400" />
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-medium">Rejoindre une societe</p>
                <p className="text-[11px] text-slate-500">Avec un code collaborateur</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Shield className="w-3 h-3 text-slate-600" />
            <span className="text-[10px] text-slate-600">Connexion securisee - Donnees chiffrees</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] text-slate-600">
            <span>FR +33 6 58 63 59 08</span>
            <span>UK +44 7591 665201</span>
          </div>
          <p className="text-[10px] text-slate-700">
            High Tech Security LTD &bull; Secutor Pro LLC
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
